import { GameState }   from '../../core/GameState.js';
import { MAINTENANCE } from '../../config/maintenance.js';
import { formatMoney, formatDate } from '../../utils/formatting.js';
import {
  daysUntil, getDrydockCost, getDrydockDays, getDrydockIntervalYears,
  getDrydockOverdueFine, getSurveyFee
} from '../../utils/maintenance.js';

/**
 * Maintenance (survey / dry dock) snippets for the Fleet and Port panels.
 */

function dueText(days) {
  if (days < 0) return `overdue by ${-days} day${days === -1 ? '' : 's'}`;
  if (days === 0) return 'due today';
  return `due in ${days} day${days === 1 ? '' : 's'}`;
}

/** One-line warning for a ship card when dry dock is close or a survey is overdue; '' otherwise. */
export function maintenanceWarningHtml(ship) {
  const m = ship.maintenance;
  if (!m || ship.status === 'drydock') return '';
  const now = GameState.currentDate;
  const parts = [];

  const dock = daysUntil(m.drydockDue, now);
  if (dock !== null && dock <= MAINTENANCE.DRYDOCK_WARNING_DAYS[0]) {
    parts.push(`Dry dock ${dueText(dock)}`);
  }
  const survey = daysUntil(m.surveyDue, now);
  if (survey !== null && survey < 0) parts.push(`Survey ${dueText(survey)}`);

  if (parts.length === 0) return '';
  const urgent = (dock !== null && dock < 0) || (survey !== null && survey < -MAINTENANCE.SURVEY_GRACE_DAYS);
  return `<span class="ship-maintenance${urgent ? ' ship-maintenance--overdue' : ''}">🔧 ${parts.join(' · ')}</span>`;
}

/** Rows for the ship details table. */
export function maintenanceRowsHtml(ship) {
  const m = ship.maintenance;
  if (!m) return '<tr><td>Survey</td><td>Scheduled at next day</td></tr>';
  const now = GameState.currentDate;
  const dock = daysUntil(m.drydockDue, now);
  const survey = daysUntil(m.surveyDue, now);
  return `
    <tr><td>Next survey</td><td>${formatDate(new Date(m.surveyDue))} (${dueText(survey)}, fee ${formatMoney(getSurveyFee(ship))})</td></tr>
    <tr><td>Next dry dock</td><td>${formatDate(new Date(m.drydockDue))} (${dueText(dock)})</td></tr>
    <tr><td>Dry dock interval</td><td>${getDrydockIntervalYears(ship)} years</td></tr>`;
}

/** Dry dock card for the port Services tab (shipyard ports only). */
export function drydockCardHtml(ship, port) {
  if (!port.services?.shipyard) return '';
  if (!ship) {
    return `<div class="svc-card svc-card-block">
      <div class="svc-card-top"><strong>Dry Dock</strong></div>
      <span class="svc-desc">Select a ship</span>
    </div>`;
  }

  const m = ship.maintenance;
  const now = GameState.currentDate;
  const cost = getDrydockCost(ship);
  const days = getDrydockDays(ship);

  let body;
  if (ship.status === 'drydock') {
    body = `<span class="svc-desc">In dry dock until ${formatDate(new Date(m.drydockUntil))}</span>`;
  } else {
    const dock = m ? daysUntil(m.drydockDue, now) : null;
    const fine = getDrydockOverdueFine(ship, now);
    const status = dock === null ? ''
      : `<span class="svc-desc${dock < 0 ? ' svc-desc--warn' : ''}">Mandatory dry dock ${dueText(dock)}${fine ? ` — fined ${formatMoney(fine)}/day` : ''}</span>`;
    const busy = ship.isBusyInPort();
    body = `${status}
      <span class="svc-desc">Hull, machinery and class survey — restores condition to 100%. Takes ${days} days.</span>
      <div class="svc-action-row">
        <span class="svc-cost-label">${formatMoney(cost)}</span>
        <button class="btn btn-sm btn-primary" id="btn-drydock"${busy ? ' disabled title="Finish loading and refuelling first"' : ''}>Dry Dock</button>
      </div>`;
  }

  return `<div class="svc-card svc-card-block">
    <div class="svc-card-top"><strong>Dry Dock</strong></div>
    ${body}
  </div>`;
}

/** Note on the Repair card when the ship is held on its condition. */
export function detentionNoteHtml(ship) {
  const d = ship?.maintenance?.detention;
  if (!d) return '';
  const text = d.reason === 'drydock'
    ? 'Detained — overdue for dry dock. Dry dock the ship to release it.'
    : `Detained — repair to at least ${d.minCondition}% to be released.`;
  return `<span class="svc-desc svc-desc--warn">⚠ ${text}</span>`;
}
